/**
 * OIMS v2 - Charts module
 * Chart.js historical metrics + group comparison
 */

let instanceCharts = {};
let groupChart = null;
let currentChartInstanceId = null;
let currentChartInstanceName = null;
let currentChartGroup = null;

const CHART_COLORS = [
  'rgb(54, 162, 235)',
  'rgb(255, 99, 132)',
  'rgb(75, 192, 192)',
  'rgb(255, 205, 86)',
  'rgb(153, 102, 255)',
  'rgb(255, 159, 64)',
  'rgb(201, 203, 207)',
  'rgb(46, 204, 113)',
];

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleColor: '#fff',
      bodyColor: '#fff'
    }
  },
  scales: {
    x: {
      type: 'time',
      time: {
        tooltipFormat: 'MMM d, HH:mm',
        displayFormats: { minute: 'HH:mm', hour: 'HH:mm', day: 'MMM d' }
      },
      grid: { color: 'rgba(255,255,255,0.1)' },
      ticks: { color: '#aaa', maxRotation: 0 }
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(255,255,255,0.1)' },
      ticks: { color: '#aaa' }
    }
  }
};

const GROUP_METRICS = {
  "birt_instances": ["MemoryMB", "CPUPercent", "ThreadCount", "HeapUsagePercent", "ResponseTime"],
  "wildfly_instances": ["MemoryMB", "CPUPercent", "ThreadCount", "ResponseTime"],
  "jbpm_instances": ["MemoryMB", "CPUPercent", "ThreadCount", "ResponseTime"],
  "org_instances": ["HostCPUPercent", "HostMemoryPercent", "ContainerCount", "ResponseTime"],
  "other_docker_instances": ["HostCPUPercent", "HostMemoryPercent", "ContainerCount", "ResponseTime"],
  "sql_instances": ["MemoryMB", "CPUPercent", "ThreadCount", "ResponseTime"],
  "iis_instances": ["TotalWorkerMemoryMB", "TotalCPUPercent", "TotalWorkerCount", "ResponseTime"]
};

export function initCharts(state) {
  setupCustomRangeToggle("chartTimeRange", "chartCustomRange");
  setupCustomRangeToggle("groupChartTimeRange", "groupChartCustomRange");

  document.getElementById("chartTimeRange")?.addEventListener("change", (e) => {
    if (e.target.value !== "custom") loadInstanceCharts();
  });
  document.getElementById("chartCustomFrom")?.addEventListener("change", loadInstanceCharts);
  document.getElementById("chartCustomTo")?.addEventListener("change", loadInstanceCharts);
  document.getElementById("btnRefreshCharts")?.addEventListener("click", loadInstanceCharts);
  document.getElementById("groupChartMetric")?.addEventListener("change", loadGroupChart);
  document.getElementById("groupChartTimeRange")?.addEventListener("change", (e) => {
    if (e.target.value !== "custom") loadGroupChart();
  });
  document.getElementById("groupChartCustomFrom")?.addEventListener("change", loadGroupChart);
  document.getElementById("groupChartCustomTo")?.addEventListener("change", loadGroupChart);
  document.getElementById("btnRefreshGroupChart")?.addEventListener("click", loadGroupChart);
  document.getElementById("btnGroupChart")?.addEventListener("click", () => showGroupChart(state));

  // Listen for chart request events from status module
  document.addEventListener('oims:show-charts', (e) => {
    const { instanceId, name, group } = e.detail;
    showHistoryCharts(instanceId, name, group);
  });

  // Destroy charts when modals close
  const historyModal = document.getElementById("historyChartsModal");
  if (historyModal) {
    historyModal.addEventListener("hidden.bs.modal", () => {
      Object.keys(instanceCharts).forEach(key => {
        if (instanceCharts[key]) {
          instanceCharts[key].destroy();
          delete instanceCharts[key];
        }
      });
    });
  }

  const groupModal = document.getElementById("groupChartModal");
  if (groupModal) {
    groupModal.addEventListener("hidden.bs.modal", () => {
      if (groupChart) {
        groupChart.destroy();
        groupChart = null;
      }
    });
  }

  // Destroy charts when leaving status tab
  document.addEventListener('oims:tab-change', (e) => {
    if (e.detail.tab !== 'status') {
      Object.values(instanceCharts).forEach(c => { try { c.destroy(); } catch(e) {} });
      Object.keys(instanceCharts).forEach(k => delete instanceCharts[k]);
    }
  });
}

function getTimeRangeParams(selectId, customFromId, customToId) {
  const val = document.getElementById(selectId).value;
  if (val === "custom") {
    const from = document.getElementById(customFromId).value;
    const to = document.getElementById(customToId).value;
    if (!from || !to) {
      window.toast.warning("Please select both From and To dates");
      return null;
    }
    const fromDate = new Date(from);
    const toDate = new Date(to);
    if (toDate <= fromDate) {
      window.toast.warning("End date must be after start date");
      return null;
    }
    const now = new Date();
    if (fromDate > now || toDate > now) {
      window.toast.warning("Dates cannot be in the future");
      return null;
    }
    const diffDays = (toDate - fromDate) / (1000 * 60 * 60 * 24);
    if (diffDays > 31) {
      window.toast.warning("Date range cannot exceed 31 days");
      return null;
    }
    return `hours=6&custom_start=${encodeURIComponent(fromDate.toISOString())}&custom_end=${encodeURIComponent(toDate.toISOString())}`;
  }
  return `hours=${parseInt(val) || 6}`;
}

function setupCustomRangeToggle(selectId, customRangeId) {
  const sel = document.getElementById(selectId);
  const customDiv = document.getElementById(customRangeId);
  if (!sel || !customDiv) return;
  sel.addEventListener("change", () => {
    if (sel.value === "custom") {
      customDiv.classList.remove("d-none");
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
      const toISO = (d) => d.toISOString().slice(0, 16);
      const fromInput = customDiv.querySelector("input:first-of-type");
      const toInput = customDiv.querySelector("input:last-of-type");
      if (fromInput && !fromInput.value) fromInput.value = toISO(weekAgo);
      if (toInput && !toInput.value) toInput.value = toISO(now);
    } else {
      customDiv.classList.add("d-none");
    }
  });
}

async function showHistoryCharts(instanceId, instanceName, group) {
  const modalEl = document.getElementById("historyChartsModal");
  if (!modalEl) return;

  currentChartInstanceId = instanceId;
  currentChartInstanceName = instanceName;
  currentChartGroup = group;

  const modal = new bootstrap.Modal(modalEl);
  document.getElementById("historyChartsInstance").textContent = instanceName;

  modal.show();
  await loadInstanceCharts();
}

async function loadInstanceCharts() {
  const instanceId = currentChartInstanceId;
  if (!instanceId) return;

  const timeParams = getTimeRangeParams("chartTimeRange", "chartCustomFrom", "chartCustomTo");
  if (!timeParams) return;

  const loadingEl = document.getElementById("chartsLoading");
  const containerEl = document.getElementById("chartsContainer");
  const noDataEl = document.getElementById("chartsNoData");

  loadingEl.classList.remove("d-none");
  containerEl.classList.add("d-none");
  noDataEl.classList.add("d-none");

  const metricsToFetch = GROUP_METRICS[currentChartGroup] || ["MemoryMB", "CPUPercent", "ThreadCount", "ResponseTime"];
  const isDockerGroup = ["org_instances", "other_docker_instances"].includes(currentChartGroup);

  const chartConfig = {
    "memoryChart": {
      metrics: ["MemoryMB", "TotalWorkerMemoryMB", "TotalMemoryPercent", "HostMemoryPercent"],
      labels: { "MemoryMB": "Memory (MB)", "TotalWorkerMemoryMB": "Worker Memory (MB)", "TotalMemoryPercent": "Container Memory %", "HostMemoryPercent": "Host Memory %" }
    },
    "cpuChart": {
      metrics: ["CPUPercent", "TotalCPUPercent", "HeapUsagePercent", "HostCPUPercent"],
      labels: { "CPUPercent": "CPU %", "TotalCPUPercent": "Container CPU %", "HeapUsagePercent": "Heap %", "HostCPUPercent": "Host CPU %" }
    },
    "threadsChart": {
      metrics: ["ThreadCount", "TotalWorkerCount", "ContainerCount"],
      labels: { "ThreadCount": "Threads", "TotalWorkerCount": "Workers", "ContainerCount": "Containers" }
    },
    "responseChart": {
      metrics: ["ResponseTime"],
      labels: { "ResponseTime": "Response (ms)" }
    }
  };

  try {
    if (isDockerGroup) {
      await loadDockerInstanceCharts(instanceId, timeParams, chartConfig, loadingEl, containerEl, noDataEl);
    } else {
      const metricsParam = metricsToFetch.join(",");
      const { data } = await axios.get(`/metrics/history/${instanceId}?${timeParams}&metrics=${metricsParam}`);

      loadingEl.classList.add("d-none");

      const hasData = Object.values(data.metrics || {}).some(m => m.values && m.values.length > 0);

      if (!hasData) {
        noDataEl.classList.remove("d-none");
        return;
      }

      containerEl.classList.remove("d-none");

      for (const [canvasId, config] of Object.entries(chartConfig)) {
        let rendered = false;
        for (const metricName of config.metrics) {
          const metricData = data.metrics[metricName];
          if (metricData && metricData.values && metricData.values.length > 0) {
            const label = config.labels[metricName] || metricName;
            renderInstanceChart(canvasId, metricData, label, CHART_COLORS[0]);
            rendered = true;
            break;
          }
        }
        if (!rendered) {
          renderInstanceChart(canvasId, null, config.labels[config.metrics[0]] || "No Data", CHART_COLORS[0]);
        }
      }
    }
  } catch (e) {
    loadingEl.classList.add("d-none");
    noDataEl.classList.remove("d-none");
    console.error("Failed to load charts:", e);
  }
}

async function loadDockerInstanceCharts(instanceId, timeParams, chartConfig, loadingEl, containerEl, noDataEl) {
  let hasAnyData = false;

  try {
    const { data } = await axios.get(`/metrics/history/docker/${instanceId}?${timeParams}&metric_type=cpu`);
    if (data.series && data.series.length > 0) {
      hasAnyData = true;
      renderMultiInstanceChart("cpuChart", data, "CPU %");
    } else {
      renderInstanceChart("cpuChart", null, "CPU %", CHART_COLORS[0]);
    }
  } catch (e) {
    console.error("Failed to load Docker CPU metrics:", e);
    renderInstanceChart("cpuChart", null, "CPU %", CHART_COLORS[0]);
  }

  try {
    const { data } = await axios.get(`/metrics/history/docker/${instanceId}?${timeParams}&metric_type=memory`);
    if (data.series && data.series.length > 0) {
      hasAnyData = true;
      renderMultiInstanceChart("memoryChart", data, "Memory %");
    } else {
      renderInstanceChart("memoryChart", null, "Memory %", CHART_COLORS[0]);
    }
  } catch (e) {
    console.error("Failed to load Docker memory metrics:", e);
    renderInstanceChart("memoryChart", null, "Memory %", CHART_COLORS[0]);
  }

  try {
    const { data } = await axios.get(`/metrics/history/${instanceId}?${timeParams}&metrics=ContainerCount`);
    const metricData = data.metrics?.ContainerCount;
    if (metricData && metricData.values && metricData.values.length > 0) {
      hasAnyData = true;
      renderInstanceChart("threadsChart", metricData, "Container Count", CHART_COLORS[0]);
    } else {
      renderInstanceChart("threadsChart", null, "Container Count", CHART_COLORS[0]);
    }
  } catch (e) {
    console.error("Failed to load ContainerCount:", e);
    renderInstanceChart("threadsChart", null, "Container Count", CHART_COLORS[0]);
  }

  try {
    const { data } = await axios.get(`/metrics/history/${instanceId}?${timeParams}&metrics=ResponseTime`);
    const metricData = data.metrics?.ResponseTime;
    if (metricData && metricData.values && metricData.values.length > 0) {
      hasAnyData = true;
      renderInstanceChart("responseChart", metricData, "Response (ms)", CHART_COLORS[0]);
    } else {
      renderInstanceChart("responseChart", null, "Response (ms)", CHART_COLORS[0]);
    }
  } catch (e) {
    console.error("Failed to load ResponseTime:", e);
    renderInstanceChart("responseChart", null, "Response (ms)", CHART_COLORS[0]);
  }

  loadingEl.classList.add("d-none");

  if (!hasAnyData) {
    noDataEl.classList.remove("d-none");
  } else {
    containerEl.classList.remove("d-none");
  }
}

function renderMultiInstanceChart(canvasId, data, title) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  if (instanceCharts[canvasId]) instanceCharts[canvasId].destroy();

  const datasets = (data.series || []).map((series, idx) => {
    const color = CHART_COLORS[idx % CHART_COLORS.length];
    const dataPoints = series.timestamps.map((t, i) => ({ x: new Date(t), y: series.values[i] }));

    return {
      label: series.label,
      data: dataPoints,
      borderColor: color,
      backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
      fill: false,
      tension: 0.3,
      pointRadius: 2,
      pointHoverRadius: 5
    };
  });

  instanceCharts[canvasId] = new Chart(canvas, {
    type: 'line',
    data: { datasets },
    options: {
      ...CHART_OPTIONS,
      plugins: {
        ...CHART_OPTIONS.plugins,
        legend: { display: true, position: 'top', labels: { color: '#aaa', usePointStyle: true, font: { size: 10 } } }
      },
      scales: {
        ...CHART_OPTIONS.scales,
        y: { ...CHART_OPTIONS.scales.y, title: { display: true, text: data.unit || '', color: '#aaa' } }
      }
    }
  });
}

function renderInstanceChart(canvasId, metricData, label, color) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  if (instanceCharts[canvasId]) instanceCharts[canvasId].destroy();

  if (!metricData || !metricData.timestamps || metricData.timestamps.length === 0) {
    instanceCharts[canvasId] = new Chart(canvas, {
      type: 'line',
      data: { datasets: [] },
      options: {
        ...CHART_OPTIONS,
        plugins: { ...CHART_OPTIONS.plugins, title: { display: true, text: 'No data available', color: '#888' } }
      }
    });
    return;
  }

  const dataPoints = metricData.timestamps.map((t, i) => ({ x: new Date(t), y: metricData.values[i] }));

  instanceCharts[canvasId] = new Chart(canvas, {
    type: 'line',
    data: {
      datasets: [{
        label,
        data: dataPoints,
        borderColor: color,
        backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
        fill: true,
        tension: 0.3,
        pointRadius: 2,
        pointHoverRadius: 5
      }]
    },
    options: {
      ...CHART_OPTIONS,
      scales: {
        ...CHART_OPTIONS.scales,
        y: { ...CHART_OPTIONS.scales.y, title: { display: true, text: metricData.unit || '', color: '#aaa' } }
      }
    }
  });
}

const METRIC_LABELS = {
  "MemoryMB": "Memory (MB)",
  "CPUPercent": "CPU %",
  "ThreadCount": "Thread Count",
  "HeapUsagePercent": "Heap Usage %",
  "ResponseTime": "Response Time",
  "FileDescriptors": "File Descriptors",
  "HostCPUPercent": "Host CPU %",
  "HostMemoryPercent": "Host Memory %",
  "ContainerCount": "Container Count",
  "TotalWorkerMemoryMB": "Worker Memory (MB)",
  "TotalCPUPercent": "CPU %",
  "TotalWorkerCount": "Worker Count",
  "ActiveConnections": "Active Connections",
};

async function showGroupChart(state) {
  const selHealthGroup = document.getElementById("selHealthGroup");
  const groupId = selHealthGroup?.value;
  const groupName = selHealthGroup?.options[selHealthGroup.selectedIndex]?.textContent || groupId;

  if (!groupId) {
    window.toast.info("Please select a service group first");
    return;
  }

  const modalEl = document.getElementById("groupChartModal");
  if (!modalEl) return;

  document.getElementById("groupChartName").textContent = groupName;

  // Populate metric dropdown based on selected group
  const metricSelect = document.getElementById("groupChartMetric");
  if (metricSelect) {
    const metrics = GROUP_METRICS[groupId] || ["MemoryMB", "CPUPercent", "ThreadCount", "ResponseTime"];
    metricSelect.innerHTML = "";
    for (const m of metrics) {
      const opt = document.createElement("option");
      opt.value = m;
      opt.textContent = METRIC_LABELS[m] || m;
      metricSelect.appendChild(opt);
    }
  }

  const modal = new bootstrap.Modal(modalEl);
  modal.show();

  await loadGroupChart();
}

async function loadGroupChart() {
  const selHealthGroup = document.getElementById("selHealthGroup");
  const groupId = selHealthGroup?.value;
  if (!groupId) return;

  const metric = document.getElementById("groupChartMetric").value;
  const timeParams = getTimeRangeParams("groupChartTimeRange", "groupChartCustomFrom", "groupChartCustomTo");
  if (!timeParams) return;

  const loadingEl = document.getElementById("groupChartLoading");
  const containerEl = document.getElementById("groupChartContainer");

  loadingEl.classList.remove("d-none");
  containerEl.classList.add("d-none");

  try {
    const { data } = await axios.get(`/metrics/history/group/${groupId}?${timeParams}&metric=${metric}`);
    loadingEl.classList.add("d-none");
    containerEl.classList.remove("d-none");
    renderGroupChart(data);
  } catch (e) {
    loadingEl.classList.add("d-none");
    containerEl.classList.remove("d-none");
    console.error("Failed to load group chart:", e);
  }
}

function renderGroupChart(data) {
  const canvas = document.getElementById("groupComparisonChart");
  if (!canvas) return;

  if (groupChart) groupChart.destroy();

  const datasets = (data.series || []).map((series, idx) => {
    const color = CHART_COLORS[idx % CHART_COLORS.length];
    const dataPoints = series.timestamps.map((t, i) => ({ x: new Date(t), y: series.values[i] }));

    return {
      label: series.label,
      data: dataPoints,
      borderColor: color,
      backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
      fill: false,
      tension: 0.3,
      pointRadius: 2,
      pointHoverRadius: 5
    };
  });

  groupChart = new Chart(canvas, {
    type: 'line',
    data: { datasets },
    options: {
      ...CHART_OPTIONS,
      plugins: {
        ...CHART_OPTIONS.plugins,
        legend: { display: true, position: 'top', labels: { color: '#aaa', usePointStyle: true } }
      },
      scales: {
        ...CHART_OPTIONS.scales,
        y: { ...CHART_OPTIONS.scales.y, title: { display: true, text: data.unit || '', color: '#aaa' } }
      }
    }
  });
}
